import { TextFieldProps, useMediaQuery, useTheme } from '@mui/material';
import TextFieldContent from 'components/text-field-content';
import React from 'react';
import { Controller, UseControllerProps } from 'react-hook-form';
import { getComputedLabel } from 'utils/label-required-option';

type TextFieldExtProps = TextFieldProps & {
  label: string;
  controller?: UseControllerProps<any>;
  decimalScale?: number;
  disableDecimal?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  infoText?: string;
  maxLength?: number;
  dark?: boolean;
  textAlign?: 'center' | 'left' | 'right';
  stopPropagation?: boolean;
};

const TextField: React.FC<TextFieldExtProps> = ({
  label,
  value,
  error,
  helperText,
  controller,
  startAdornment,
  endAdornment,
  infoText,
  hiddenLabel,
  hidden = false,
  stopPropagation = false,
  size = 'medium',
  textAlign, // Need to pass this down or handle it in TextFieldContent's sx
  margin = 'none',
  maxLength,
  type,
  dark = false,
  fullWidth = false,
  disabled,
  decimalScale = 3,
  disableDecimal = false,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const controlledFullWidth = fullWidth || isMobile;

  return controller ? (
    <Controller
      {...controller}
      render={({ field, fieldState }) => {
        const currentHelperText = fieldState.error?.message ?? helperText;
        const computedLabel = getComputedLabel(label, controller);

        return (
          <TextFieldContent
            {...props}
            label={computedLabel}
            value={field.value}
            error={fieldState.invalid}
            helperText={currentHelperText}
            startAdornment={startAdornment}
            endAdornment={endAdornment}
            infoText={infoText}
            hiddenLabel={hiddenLabel}
            stopPropagation={stopPropagation}
            size={size}
            margin={margin}
            textAlign={textAlign}
            type={type}
            dark={dark}
            fullWidth={controlledFullWidth}
            disabled={disabled}
            decimalScale={decimalScale}
            disableDecimal={disableDecimal}
            onFocus={(event) => {
              event.target.select();
              props.onFocus && props.onFocus(event);
            }}
            onBlur={(data) => {
              field.onBlur();
              props.onBlur && props.onBlur(data);
            }}
            onChange={(data) => {
              field.onChange(data);
              props.onChange && props.onChange(data);
            }}
            onKeyDown={props.onKeyDown}
          />
        );
      }}
    />
  ) : (
    <>
      <TextFieldContent
        {...props}
        label={label}
        value={value}
        error={error}
        helperText={helperText}
        startAdornment={startAdornment}
        endAdornment={endAdornment}
        infoText={infoText}
        hiddenLabel={hiddenLabel}
        textAlign={textAlign}
        stopPropagation={stopPropagation}
        size={size}
        margin={margin}
        type={type}
        dark={dark}
        fullWidth={controlledFullWidth}
        disabled={disabled}
        decimalScale={decimalScale}
        disableDecimal={disableDecimal}
        onFocus={(event) => {
          event.target.select();
          props.onFocus && props.onFocus(event);
        }}
        onBlur={props.onBlur}
        onChange={props.onChange}
        onKeyDown={props.onKeyDown}
      />
    </>
  );
};

export default React.memo(TextField);
